import React, { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Divider, Box, Typography, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { ROUTES, USER_ROLES } from '../../../shared/constants';
import { loadOrderOrganization, selectOrdersSearchOrderOrganization } from '../../redux/slice';
import { selectCurrentUserIsAdmin, selectCurrentUserIsPortalAdmin, selectCurrentUserRole } from '../../../auth/redux/slice';

export type CreateOrderIntentModalProps = {
    open: boolean;
    onClose: () => void;
};

// Pre-Buy - intent modal shown from Orders "Create New Order". Flow 1 (Ship to Clinic, Phase 2),
// Flow 2a/2b-i/2b-ii (Ship to Patient, Phase 3), and Flow 3 (Clinic Submission, Phase 4) each have
// their own dedicated page.
export const CreateOrderIntentModal: React.FC<CreateOrderIntentModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isAdmin = useSelector(selectCurrentUserIsAdmin);
    const roleName = useSelector(selectCurrentUserRole);
    const ownOrg = useSelector(selectOrdersSearchOrderOrganization);
    // isAdmin is also true for Organization Admin(+Provider) - they belong to a single, known org
    // (same distinction OrdersSearchActions.tsx/ShipToClinic.tsx make), so gate on their own org's
    // flag too. Only a true multi-org admin (who hasn't picked an org yet) stays unconditionally
    // enabled here - the per-org gate is enforced on the Ship to Clinic page's org dropdown instead.
    const isTrueAdmin = isAdmin && roleName !== USER_ROLES.OrganizationAdminProvider && roleName !== USER_ROLES.OrganizationAdmin;
    const preBuyDisabled = !isTrueAdmin && !ownOrg?.preBuyEnabled;
    // Sep 2026 - "Create a standard order" is now restricted to Portal Admin specifically, not
    // every true (multi-org) admin - a User Group Admin or generic Admin used to see this too
    // just by virtue of isTrueAdmin. Deliberately separate from isTrueAdmin above, which still
    // drives the Pre-Buy kit options' own enablement and is unrelated to this restriction.
    const isPortalAdmin = useSelector(selectCurrentUserIsPortalAdmin);

    useEffect(() => {
        if (open) {
            dispatch(loadOrderOrganization());
        }
    }, [dispatch, open]);

    const handleSelect = (route: string) => {
        onClose();
        navigate(route);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: '10px' } }}
        >
            <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#1A2332', pb: 1 }}>
                Create New Order
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px', pt: 2.5, pb: 2.5 }}>
                <Tooltip title={preBuyDisabled ? 'Ordering testing supplies is not enabled for your organization.' : ''}>
                    <span>
                        <IntentOption
                            icon={<LocalShippingOutlinedIcon />}
                            title="Order Testing Supplies"
                            description="Bulk order testing supplies to your clinic."
                            onClick={() => handleSelect(ROUTES.KitOrderShipToClinic)}
                            disabled={preBuyDisabled}
                        />
                    </span>
                </Tooltip>
                <Tooltip title={preBuyDisabled ? 'Ordering testing supplies is not enabled for your organization.' : ''}>
                    <span>
                        <IntentOption
                            icon={<HomeOutlinedIcon />}
                            title="At-Home Testing"
                            description="Ship an At-Home test directly to a patient's home."
                            onClick={() => handleSelect(ROUTES.ShipToPatient)}
                            disabled={preBuyDisabled}
                        />
                    </span>
                </Tooltip>
                {/* Aug 2026 - no longer gated on the org actually having an unused kit
                    (selectKitsHasUnusedKit) - the Clinic Submission page's "Legacy Kit" manual
                    entry now covers that case, so an org with none isn't blocked from getting
                    here at all, just prompted to enter its kit number by hand once there. */}
                <Tooltip title={preBuyDisabled ? 'Ordering testing supplies is not enabled for your organization.' : ''}>
                    <span>
                        <IntentOption
                            icon={<ScienceOutlinedIcon />}
                            title="New Test Submission"
                            description="Submit a test using the test supplies at your clinic."
                            onClick={() => handleSelect(ROUTES.ClinicSubmission)}
                            disabled={preBuyDisabled}
                        />
                    </span>
                </Tooltip>
                {isPortalAdmin && (
                    <IntentOption
                        icon={<DescriptionOutlinedIcon />}
                        title="Create a standard order"
                        description="The regular order form — for tests not tied to testing supplies."
                        onClick={() => handleSelect(ROUTES.CreateOrder)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

type IntentOptionProps = {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
    disabled?: boolean;
};

const IntentOption: React.FC<IntentOptionProps> = ({ icon, title, description, onClick, disabled }) => (
    <Box
        onClick={disabled ? undefined : onClick}
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 14px',
            borderRadius: '8px',
            border: '1px solid #DDE4EC',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            '&:hover': disabled ? {} : { borderColor: '#0891B2', backgroundColor: '#F0FAFB' },
            '& svg': { fontSize: '20px', color: '#0891B2', mt: '2px' },
        }}
    >
        {icon}
        <Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#1A2332' }}>{title}</Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#64748B', mt: '2px' }}>{description}</Typography>
        </Box>
    </Box>
);
